import Cookies from "js-cookie"
import { jwtDecode } from "jwt-decode"

import { env } from "@/lib/env"
import { storageLogger } from "@/lib/logger"

interface IJwtPayload {
  exp?: number
  iat?: number
  sub?: string
}

/**
 * Secure client-side storage utility for sensitive tokens.
 * Relies on cookies (with environment-aware settings) and falls back to localStorage when needed.
 */

const ACCESS_TOKEN_KEY = "hwcompanyToken"
const REFRESH_TOKEN_KEY = "hwcompanyRefreshToken"

const TOKEN_SETTINGS = {
  access: { key: ACCESS_TOKEN_KEY, expires: 1 },
  refresh: { key: REFRESH_TOKEN_KEY, expires: 30 },
} as const

const REFRESH_TOKEN_PATH = "/"

type TTokenKind = keyof typeof TOKEN_SETTINGS

let insecureProtocolWarningLogged = false

const potentialIpAddress = /^\d{1,3}(?:\.\d{1,3}){3}$/

function resolveCookieDomain(hostname: string | undefined): string | undefined {
  if (!hostname) return undefined
  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".local") ||
    potentialIpAddress.test(hostname)
  ) {
    return undefined
  }

  return hostname
}

function buildBaseCookieAttributes(
  isRefreshToken = false,
): Cookies.CookieAttributes {
  const base: Cookies.CookieAttributes = {
    path: isRefreshToken ? REFRESH_TOKEN_PATH : "/",
    sameSite: "lax", // Use 'lax' for better compatibility
  }

  if (!isBrowser()) {
    return base
  }

  const { protocol, hostname } = window.location
  const isHttps = protocol === "https:"
  const isLocalhost =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".local")

  if (isHttps) {
    base.secure = true
  } else {
    // For HTTP (development), don't use secure flag
    if (!isLocalhost && env.isProduction && !insecureProtocolWarningLogged) {
      storageLogger.warn(
        "Aplicação em produção sem HTTPS: cookies podem ser bloqueados pelo navegador",
      )
      insecureProtocolWarningLogged = true
    }
  }

  // Only set domain for production domains
  const domain = resolveCookieDomain(hostname)
  if (domain) {
    base.domain = domain
  }

  return base
}

// Cookie configuration based on environment
const getCookieOptions = (isRefreshToken = false): Cookies.CookieAttributes => {
  const tokenType: TTokenKind = isRefreshToken ? "refresh" : "access"
  const baseOptions = buildBaseCookieAttributes(isRefreshToken)

  return {
    ...baseOptions,
    expires: TOKEN_SETTINGS[tokenType].expires,
  }
}

function persistTokenCookie(tokenType: TTokenKind, token: string): boolean {
  if (!isBrowser()) return false

  const { key } = TOKEN_SETTINGS[tokenType]
  const options = getCookieOptions(tokenType === "refresh")

  try {
    Cookies.set(key, token, options)

    const storedValue = Cookies.get(key) ?? null
    const success = Boolean(storedValue)

    if (!success && env.isDevelopment) {
      storageLogger.warn(`Falha ao persistir ${tokenType} token em cookie`, {
        key,
        options,
        tokenLength: token.length,
      })
    }

    return success
  } catch (error) {
    storageLogger.error(`Erro ao gravar ${tokenType} token em cookie`, error)
    return false
  }
}

function readTokenCookie(tokenType: TTokenKind): string | null {
  if (!isBrowser()) return null

  const { key } = TOKEN_SETTINGS[tokenType]
  return Cookies.get(key) ?? null
}

function removeTokenCookie(tokenType: TTokenKind): void {
  if (!isBrowser()) return

  const { key } = TOKEN_SETTINGS[tokenType]
  const isRefreshToken = tokenType === "refresh"
  const removalOptions = buildBaseCookieAttributes(isRefreshToken)

  Cookies.remove(key, removalOptions)
}

/**
 * Check if we're in a browser environment
 */
function isBrowser(): boolean {
  return typeof window !== "undefined"
}

/**
 * Validate token format for basic security
 */
function validateToken(token: string, type: "access" | "refresh"): boolean {
  if (!token || typeof token !== "string") {
    if (env.isDevelopment) {
      storageLogger.warn(
        `${type} token validation failed: invalid token type`,
        {
          token,
          type: typeof token,
        },
      )
    }
    return false
  }

  // Basic length validation (JWTs are typically 100+ chars)
  if (token.length < 50) {
    if (env.isDevelopment) {
      storageLogger.warn(`${type} token validation failed: too short`, {
        length: token.length,
        required: 50,
      })
    }
    return false
  }

  // Basic JWT format check (should have 3 parts separated by dots)
  const parts = token.split(".")
  if (parts.length !== 3) {
    if (env.isDevelopment) {
      storageLogger.warn(
        `${type} token validation failed: invalid JWT format`,
        {
          parts: parts.length,
          required: 3,
        },
      )
    }
    return false
  }

  // Check for suspicious patterns
  if (token.includes("<script") || token.includes("javascript:")) {
    storageLogger.warn(`Suspicious token detected for ${type}`)
    return false
  }

  // Decode and check expiration
  try {
    const decoded = jwtDecode<IJwtPayload>(token)

    // Check if token is already expired
    if (decoded.exp) {
      const now = Math.floor(Date.now() / 1000)
      if (decoded.exp < now) {
        if (env.isDevelopment) {
          storageLogger.warn(`${type} token is already expired`, {
            exp: decoded.exp,
            now,
            diff: decoded.exp - now,
          })
        }
        return false
      }
    }

    // Check if token is from the future (clock skew)
    if (decoded.iat) {
      const now = Math.floor(Date.now() / 1000)
      const maxClockSkew = 300 // 5 minutes
      if (decoded.iat > now + maxClockSkew) {
        if (env.isDevelopment) {
          storageLogger.warn(`${type} token issued in the future`, {
            iat: decoded.iat,
            now,
            diff: decoded.iat - now,
          })
        }
        return false
      }
    }
  } catch (error) {
    if (env.isDevelopment) {
      storageLogger.warn(`${type} token validation failed: decode error`, error)
    }
    return false
  }

  return true
}

/**
 * Set tokens using secure cookies
 * For maximum security, these should be set as httpOnly cookies by the server
 */
function setTokens(accessToken: string, refreshToken?: string): void {
  if (!isBrowser()) return

  // Validate tokens before storing
  if (!validateToken(accessToken, "access")) {
    storageLogger.error("Invalid access token format")
    return
  }

  if (refreshToken && !validateToken(refreshToken, "refresh")) {
    storageLogger.error("Invalid refresh token format")
    return
  }

  try {
    const accessPersisted = persistTokenCookie("access", accessToken)
    const refreshPersisted = refreshToken
      ? persistTokenCookie("refresh", refreshToken)
      : true

    if (!accessPersisted || (refreshToken && !refreshPersisted)) {
      // Remove any partially written cookies
      removeTokenCookie("access")
      if (refreshToken) {
        removeTokenCookie("refresh")
      }

      // In development, allow localStorage fallback for convenience
      if (env.isDevelopment) {
        storageLogger.warn(
          "⚠️ Cookies blocked - using localStorage fallback for development",
          {
            accessPersisted,
            refreshPersisted,
            protocol: window.location.protocol,
            hostname: window.location.hostname,
            cookiesEnabled: navigator.cookieEnabled,
          },
        )

        // Use localStorage as fallback in development only
        try {
          localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
          if (refreshToken) {
            localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
          }
          return
        } catch (storageError) {
          storageLogger.error("localStorage also blocked", storageError)
        }
      }

      // In production, show error to user - cookies are required
      storageLogger.error(
        "Cookies are blocked. Cannot store authentication tokens.",
        {
          accessPersisted,
          refreshPersisted,
          protocol: window.location.protocol,
          hostname: window.location.hostname,
          cookiesEnabled: navigator.cookieEnabled,
        },
      )

      // Dispatch event for UI to show warning
      if (typeof window !== "undefined") {
        const event = new CustomEvent("auth-storage-error", {
          detail: {
            message:
              "Your browser is blocking cookies. Please enable cookies to use this application.",
            action: "enable-cookies",
          },
        })
        window.dispatchEvent(event)
      }

      throw new Error("Cookie storage blocked - authentication not possible")
    }

    if (env.isDevelopment) {
      storageLogger.debug("Tokens armazenados com sucesso em cookies")
    }
  } catch (error) {
    storageLogger.error("Erro ao armazenar tokens em cookies", error)
    throw error // Don't silently fail
  }
}

/**
 * Get tokens from secure cookies
 */
function getTokens(): { access: string | null; refresh: string | null } {
  if (!isBrowser()) {
    return { access: null, refresh: null }
  }

  try {
    const access = readTokenCookie("access")
    const refresh = readTokenCookie("refresh")

    // In development, check localStorage fallback if cookies are empty
    if (env.isDevelopment && !access && !refresh) {
      try {
        const fallbackAccess = localStorage.getItem(ACCESS_TOKEN_KEY)
        const fallbackRefresh = localStorage.getItem(REFRESH_TOKEN_KEY)

        if (fallbackAccess || fallbackRefresh) {
          storageLogger.debug(
            "Reading tokens from localStorage (development fallback)",
          )
          return {
            access: fallbackAccess,
            refresh: fallbackRefresh,
          }
        }
      } catch (storageError) {
        storageLogger.debug("Could not read from localStorage", storageError)
      }
    }

    return { access, refresh }
  } catch (error) {
    storageLogger.error("Failed to retrieve tokens from cookies", error)
    return { access: null, refresh: null }
  }
}

/**
 * Clear all stored tokens from cookies and fallback storages
 */
function clearTokens(): void {
  if (!isBrowser()) return

  try {
    // Remove cookies
    removeTokenCookie("access")
    removeTokenCookie("refresh")

    // Also clear from legacy storages for complete cleanup
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  } catch (error) {
    storageLogger.error("Failed to clear tokens", error)
  }
}

/**
 * Check if access token exists in cookies
 */
function hasAccessToken(): boolean {
  const { access } = getTokens()
  return Boolean(access)
}

/**
 * Get cookie expiration time for a given token
 * Useful for token refresh logic
 */
function getTokenExpiration(tokenType: "access" | "refresh"): Date | null {
  if (!isBrowser()) return null

  try {
    const token = readTokenCookie(tokenType)

    if (!token) return null

    const decoded = jwtDecode<IJwtPayload>(token)

    if (!decoded.exp) return null

    // JWT exp is in seconds, convert to milliseconds
    return new Date(decoded.exp * 1000)
  } catch (error) {
    storageLogger.error("Failed to get token expiration", error)
    return null
  }
}

/**
 * Diagnose cookie storage issues
 * Useful for debugging storage problems
 *
 * Usage example (in browser console):
 * ```
 * import { secureStorage } from '@/lib/secure-storage'
 * console.table(secureStorage.diagnoseStorage())
 * ```
 */
function diagnoseStorage(): {
  cookiesEnabled: boolean
  localStorageAvailable: boolean
  protocol: string
  hostname: string
  hasCookies: boolean
  hasLocalStorage: boolean
  recommendations: string[]
} {
  if (!isBrowser()) {
    return {
      cookiesEnabled: false,
      localStorageAvailable: false,
      protocol: "unknown",
      hostname: "unknown",
      hasCookies: false,
      hasLocalStorage: false,
      recommendations: ["Not running in browser environment"],
    }
  }

  const recommendations: string[] = []
  const { protocol, hostname } = window.location
  const cookiesEnabled = navigator.cookieEnabled

  // Check localStorage
  let localStorageAvailable = false
  try {
    const testKey = "__storage_test__"
    localStorage.setItem(testKey, "test")
    localStorage.removeItem(testKey)
    localStorageAvailable = true
  } catch {
    localStorageAvailable = false
    recommendations.push("LocalStorage está bloqueado ou indisponível")
  }

  // Check cookies
  const tokens = getTokens()
  const hasCookies = Boolean(tokens.access && readTokenCookie("access"))
  const hasLocalStorage = Boolean(tokens.access && !readTokenCookie("access"))

  if (!cookiesEnabled) {
    recommendations.push("Cookies estão desabilitados no navegador")
  }

  if (protocol !== "https:" && !hostname.includes("localhost")) {
    recommendations.push(
      "Aplicação rodando em HTTP (não HTTPS) - cookies podem ser bloqueados",
    )
  }

  if (!hasCookies && tokens.access) {
    recommendations.push(
      "Tokens estão sendo armazenados em localStorage (fallback)",
    )
  }

  if (
    hostname !== "localhost" &&
    !hostname.includes(".") &&
    hostname !== "127.0.0.1"
  ) {
    recommendations.push(
      "Hostname inválido para cookies - use localhost ou um domínio completo",
    )
  }

  return {
    cookiesEnabled,
    localStorageAvailable,
    protocol,
    hostname,
    hasCookies,
    hasLocalStorage,
    recommendations,
  }
}

export const secureStorage = {
  setTokens,
  getTokens,
  clearTokens,
  hasAccessToken,
  getTokenExpiration,
  diagnoseStorage,
}
